/**
 * 视口高度监听工具
 * 解决移动端浏览器中 100vh 大于实际可视区域的问题
 */

/**
 * 获取真实的视口高度
 * @returns 真实的视口高度（像素）
 */
export function getRealViewportHeight(): number {
  return window.visualViewport == null
    ? window.innerHeight
    : window.visualViewport.height
}

/**
 * 设置CSS变量 --vh 为真实的视口高度
 */
export function setViewportHeightCSSVar(): void {
  const vh = getRealViewportHeight()
  document.documentElement.style.setProperty('--vh', `${vh}px`)
}

/**
 * 初始化视口高度监听
 * 监听窗口大小变化，动态更新 --vh CSS变量
 */
export function initViewportHeightListener(): () => void {
  // 初始设置
  setViewportHeightCSSVar()

  // 监听窗口大小变化
  const handleResize = () => {
    setViewportHeightCSSVar()
  }

  // 监听方向变化（移动端）
  const handleOrientationChange = () => {
    // 延迟执行，等待浏览器完成方向变化
    setTimeout(() => {
      setViewportHeightCSSVar()
    }, 100)
  }

  // 添加事件监听器
  window.addEventListener('resize', handleResize)
  window.addEventListener('orientationchange', handleOrientationChange)

  // 返回清理函数
  return () => {
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('orientationchange', handleOrientationChange)
  }
}

/**
 * React Hook: 使用视口高度监听
 * @returns 当前的视口高度值
 */
export function useViewportHeight(): number {
  const [viewportHeight, setViewportHeight] = React.useState<number>(0)

  React.useEffect(() => {
    // 初始设置
    const initialHeight = getRealViewportHeight()
    setViewportHeight(initialHeight)

    // 初始化监听器
    const cleanup = initViewportHeightListener()

    // 监听自定义事件（可选，用于手动触发更新）
    const handleViewportUpdate = () => {
      setViewportHeight(getRealViewportHeight())
    }

    window.addEventListener('viewportheightupdate', handleViewportUpdate)

    return () => {
      cleanup()
      window.removeEventListener('viewportheightupdate', handleViewportUpdate)
    }
  }, [])

  return viewportHeight
}

// 导入React（如果使用Hook）
import React from 'react'
